// Cliente 360 payment map: payment concentration overview with a count-based
// heatmap for dense sets and simple points for small sets. OSM tiles always
// keep their original theme because inverted cartography loses legibility.
import { useEffect, useRef } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Tooltip, theme } from 'antd';
import { DashboardCard } from '../../../components/DashboardCard';
import { EmptyState } from '../../../components/EmptyState';
import { usePagoCoordenadas } from '../../pagos/api/usePagos';
import { useT } from '../../../locales';
import { coordenadasValidas } from '../../../lib/geo';
import { useLeaflet } from '../../../lib/useLeaflet';
import { PIN_ANCHOR, PIN_SIZE, pinHtml } from '../../../lib/pin';
import { debeUsarHeatmap, gradienteHeat } from '../cliente-servicios';
import './mapa-pagos.css';

const ALTURA_MAPA = 420;
const ZOOM_PUNTO_UNICO = 14;
const PADDING_FRACCION = 0.2;
// Payment points usually fall in the same block; keep tile loading bounded.
const MAX_ZOOM_TILES = 17;

interface MapaPagosClienteProps {
  clienteId: number;
}

export function MapaPagosCliente({ clienteId }: MapaPagosClienteProps) {
  const t = useT();
  const { token } = theme.useToken();
  const L = useLeaflet();
  const mapRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, error } = usePagoCoordenadas(clienteId);

  const puntos = (data?.data ?? []).flatMap(({ latitud, longitud }) => {
    const coordenada = coordenadasValidas(latitud, longitud);
    return coordenada ? [{ latitud: coordenada.lat, longitud: coordenada.lng }] : [];
  });

  useEffect(() => {
    if (!L || !mapRef.current || puntos.length === 0) return;

    const map = L.map(mapRef.current, {
      zoomControl: true,
      maxZoom: MAX_ZOOM_TILES,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: MAX_ZOOM_TILES,
    }).addTo(map);

    const bounds = L.latLngBounds([]);
    for (const p of puntos) bounds.extend([p.latitud, p.longitud]);

    if (debeUsarHeatmap(puntos.length)) {
      // Count-based heatmap: every payment contributes exactly one unit of
      // intensity. The payment amount must never change the map meaning.
      const gradiente = gradienteHeat(token.colorPrimary, 'light');
      const heat = L.heatLayer(
        puntos.map((p) => [p.latitud, p.longitud, 1] as [number, number, number]),
        {
          radius: 28,
          blur: 20,
          maxZoom: MAX_ZOOM_TILES,
          minOpacity: 0.25,
          gradient: gradiente.stops,
        },
      );
      heat.addTo(map);
    } else {
      const pinIcon = L.divIcon({
        className: '',
        html: pinHtml(token.colorPrimary),
        iconSize: PIN_SIZE,
        iconAnchor: PIN_ANCHOR,
      });

      for (const p of puntos) {
        L.marker([p.latitud, p.longitud], { icon: pinIcon, interactive: false }).addTo(map);
      }
    }

    if (puntos.length === 1) {
      map.setView([puntos[0]!.latitud, puntos[0]!.longitud], ZOOM_PUNTO_UNICO);
    } else if (bounds.isValid()) {
      const rect = mapRef.current.getBoundingClientRect();
      const padding = [
        Math.round(rect.width * PADDING_FRACCION),
        Math.round(rect.height * PADDING_FRACCION),
      ] as [number, number];
      map.fitBounds(bounds, { padding });
    }

    return () => {
      map.remove();
    };
  }, [L, puntos, token.colorPrimary]);

  const gradiente = gradienteHeat(token.colorPrimary, 'light');
  const esHeatmap = debeUsarHeatmap(puntos.length);
  const intensidadAyuda = t('pages.clientes.servicios.mapaHelp');
  const intensidadAyudaLabel = t('pages.clientes.servicios.mapaHelpLabel');

  return (
    <DashboardCard
      title={t('pages.clientes.servicios.mapaTitle')}
      subtitle={t('pages.clientes.servicios.mapaSubtitle', { count: puntos.length })}
      extra={
        <Tooltip title={intensidadAyuda}>
          <Button
            type="text"
            size="small"
            shape="circle"
            aria-label={intensidadAyudaLabel}
            icon={<InfoCircleOutlined aria-hidden="true" />}
          />
        </Tooltip>
      }
      loading={isLoading}
      bodyStyle={{
        padding: 0,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {error ? (
        <EmptyState
          compact
          title={t('pages.clientes.servicios.mapaErrorLoad')}
          description={t('pages.clientes.servicios.mapaErrorLoadDesc')}
        />
      ) : puntos.length === 0 ? (
        <EmptyState
          compact
          title={t('pages.clientes.servicios.sinPagosGeo')}
          description={t('pages.clientes.servicios.sinPagosGeoDesc')}
        />
      ) : (
        <div
          ref={mapRef}
          data-testid="mapa-pagos"
          className="mapa-pagos"
          style={{
            flex: 1,
            minHeight: ALTURA_MAPA,
            width: '100%',
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: '0 0 12px 12px',
            // Theme token for the intensity legend border.
            ['--mapa-leyenda-border' as string]: token.colorBorder,
          }}
        >
          {esHeatmap && (
            <div
              className="mapa-pagos__leyenda"
              style={{
                background: token.colorBgElevated,
                color: token.colorText,
                border: `1px solid ${token.colorBorder}`,
              }}
            >
              <span>{t('pages.clientes.servicios.mapaLeyendaBaja')}</span>
              <div
                aria-hidden
                style={{
                  width: 56,
                  height: 8,
                  borderRadius: 4,
                  background: `linear-gradient(to right, ${gradiente.bajo}, ${gradiente.medio}, ${gradiente.alto})`,
                }}
              />
              <span>{t('pages.clientes.servicios.mapaLeyendaAlta')}</span>
            </div>
          )}
        </div>
      )}
    </DashboardCard>
  );
}
